import { FileText, Wrench, ListChecks, FolderOpen } from 'lucide-react'
import type { Dataset, Property, FinancialPeriod, WorkOrder, ActionItem } from '@/types/entities'
import { SectionCard, EmptyState, Badge, DataTable, Button } from '@/components/ui'
import type { Column } from '@/components/ui'
import { PnLTable } from '@/components/finance/PnLTable'
import { Timeline } from '@/components/timeline/Timeline'
import {
  workOrdersForProperty, actionItemsForProperty, activitiesForRef, latest,
} from '@/lib/selectors'
import { money, num, pct, date } from '@/lib/format'
import { delinquencyRead } from './propertyMetrics'

/* ----------------------------- Financials ----------------------------- */
export function FinancialsTab({ p, fp }: { p: Property; fp?: FinancialPeriod }) {
  if (!fp) {
    return (
      <SectionCard eyebrow="P&L" title="Financial Performance">
        <EmptyState
          icon={<FileText size={26} />}
          title="No financial period on file"
          hint={`No actual-vs-budget P&L has been loaded for ${p.name}. Quarterly financials are currently mirrored for the JPMorgan JV portfolio only.`}
        />
      </SectionCard>
    )
  }
  return (
    <SectionCard
      eyebrow="P&L"
      title={`Operating Statement · ${fp.period}`}
      right={<Badge tone="neutral" variant="outline">{fp.basis === 'accrual' ? 'Accrual' : 'Cash'} · {fp.source?.split('(')[0]?.trim() ?? 'GL'}</Badge>}
    >
      <PnLTable fp={fp} />
    </SectionCard>
  )
}

/* ----------------------------- Vital Factors ----------------------------- */
export function VitalFactorsTab({ p, fp }: { p: Property; fp?: FinancialPeriod }) {
  const occ = latest(p.occupancyPct)
  const del = delinquencyRead(fp)
  const rating = latest(p.googleRating)
  const revLatest = latest(p.revenue)
  const ratePerNrsf = p.nrsf && revLatest ? (revLatest * 12) / p.nrsf : undefined

  const tiles: VitalTile[] = []
  if (occ != null) tiles.push({ label: 'Physical Occupancy', value: pct(occ), raw: occ, bench: 90, higherBetter: true, unit: '%' })
  if (del.pct != null) tiles.push({ label: 'Delinquency', value: pct(del.pct), raw: del.pct, bench: 8, higherBetter: false, unit: '% rent' })
  if (ratePerNrsf != null) tiles.push({ label: 'Revenue / NRSF', value: `$${ratePerNrsf.toFixed(2)}`, raw: ratePerNrsf, bench: ratePerNrsf, higherBetter: true, unit: '$/sf/yr' })
  if (rating != null) tiles.push({ label: 'Google Rating', value: rating.toFixed(1), raw: rating, bench: 4.5, higherBetter: true, unit: '/ 5.0' })

  return (
    <SectionCard eyebrow="Scorecard" title="Vital Factors">
      {tiles.length > 0 ? (
        <>
          <div className="pd-stats">
            {tiles.map((t) => <VitalCard key={t.label} t={t} />)}
          </div>
          <p className="pd-caption">
            Benchmarks: 90% stabilized occupancy · ≤8% delinquency · 4.5 reputation. Bars compare the asset to target.
          </p>
        </>
      ) : (
        <EmptyState title="No vital factors available" hint="No occupancy, financial, or reputation signals on file." />
      )}
    </SectionCard>
  )
}

interface VitalTile { label: string; value: string; raw: number; bench: number; higherBetter: boolean; unit: string }

function VitalCard({ t }: { t: VitalTile }) {
  // fill % of bar relative to a sensible scale; tone vs benchmark
  const scaleMax = t.higherBetter ? Math.max(t.bench, t.raw) * 1.1 : Math.max(t.bench * 2, t.raw)
  const fillPct = Math.max(4, Math.min(100, (t.raw / scaleMax) * 100))
  const benchPct = Math.max(0, Math.min(100, (t.bench / scaleMax) * 100))
  const meets = t.higherBetter ? t.raw >= t.bench : t.raw <= t.bench
  const near = t.higherBetter ? t.raw >= t.bench * 0.95 : t.raw <= t.bench * 1.5
  const toneCls = meets ? 'ok' : near ? 'warn' : 'alert'
  return (
    <div className="pd-vital">
      <span className="pd-vital-label">{t.label}</span>
      <span className="pd-vital-val num">{t.value}</span>
      <div className="pd-vital-bar">
        <div className={`pd-vital-bar-fill pd-vital-bar-fill--${toneCls}`} style={{ width: `${fillPct}%` }} />
        <div className="pd-vital-bar-bench" style={{ left: `${benchPct}%` }} />
      </div>
      <span className="pd-vital-bench">Target {t.higherBetter ? '≥' : '≤'} {t.bench}{t.unit.startsWith('%') || t.unit === '% rent' ? '%' : ''}</span>
    </div>
  )
}

/* ----------------------------- Work Orders ----------------------------- */
export function WorkOrdersTab({ d, p }: { d: Dataset; p: Property }) {
  const wos = workOrdersForProperty(d, p.id)
  const statusTone: Record<string, 'ok' | 'warn' | 'alert' | 'neutral' | 'info'> = {
    Completed: 'ok', 'In Progress': 'info', Dispatched: 'info', Open: 'warn', Cancelled: 'neutral',
  }
  const cols: Column<WorkOrder>[] = [
    { key: 'number', header: 'WO #', width: 120, render: (w) => <span className="num">{w.number}</span>, sortValue: (w) => w.number },
    { key: 'title', header: 'Description', render: (w) => <span className="truncate">{w.title}</span> },
    { key: 'trade', header: 'Trade', width: 140, render: (w) => w.trade ?? '—', sortValue: (w) => w.trade ?? '' },
    { key: 'vendor', header: 'Vendor', width: 170, render: (w) => w.vendor ?? '—' },
    { key: 'status', header: 'Status', width: 120, render: (w) => <Badge tone={statusTone[w.status] ?? 'neutral'} variant="soft">{w.status}</Badge>, sortValue: (w) => w.status },
    { key: 'opened', header: 'Opened', align: 'right', width: 100, render: (w) => date(w.openedAt, 'short'), sortValue: (w) => w.openedAt ?? '' },
    {
      key: 'cost', header: 'Cost', align: 'right', width: 110,
      render: (w) => <span className="num">{money(w.actualCost ?? w.estCost)}</span>,
      sortValue: (w) => w.actualCost ?? w.estCost ?? 0,
    },
  ]
  return (
    <SectionCard eyebrow="ServiceChannel" title="Work Orders" right={<span className="pd-muted-row">{wos.length} total</span>}>
      {wos.length > 0 ? (
        <DataTable<WorkOrder>
          columns={cols}
          rows={wos}
          getRowKey={(w) => w.id}
          initialSort={{ key: 'opened', dir: 'desc' }}
          dense
        />
      ) : (
        <EmptyState icon={<Wrench size={26} />} title="No work orders" hint="No ServiceChannel tickets mirrored for this asset." />
      )}
    </SectionCard>
  )
}

/* ----------------------------- Action Items ----------------------------- */
export function ActionItemsTab({ d, p }: { d: Dataset; p: Property }) {
  const items = actionItemsForProperty(d, p.id)
  const prioTone: Record<string, 'ok' | 'warn' | 'alert' | 'neutral' | 'info'> = {
    Critical: 'alert', High: 'warn', Medium: 'info', Low: 'neutral',
  }
  const statusTone: Record<string, 'ok' | 'warn' | 'alert' | 'neutral' | 'info'> = {
    Open: 'warn', 'In Progress': 'info', Blocked: 'alert', Closed: 'ok',
  }
  const cols: Column<ActionItem>[] = [
    { key: 'title', header: 'Action', render: (a) => a.title },
    { key: 'priority', header: 'Priority', width: 110, render: (a) => <Badge tone={prioTone[a.priority] ?? 'neutral'} variant="soft" dot>{a.priority}</Badge>, sortValue: (a) => a.priority },
    { key: 'status', header: 'Status', width: 120, render: (a) => <Badge tone={statusTone[a.status] ?? 'neutral'} variant="outline">{a.status}</Badge>, sortValue: (a) => a.status },
    { key: 'owner', header: 'Owner', width: 150, render: (a) => a.owner ?? '—' },
    { key: 'due', header: 'Due', align: 'right', width: 110, render: (a) => date(a.dueDate, 'short'), sortValue: (a) => a.dueDate ?? '' },
  ]
  return (
    <SectionCard eyebrow="Open Loops" title="Action Items" right={<span className="pd-muted-row">{items.length} attached</span>}>
      {items.length > 0 ? (
        <DataTable<ActionItem>
          columns={cols}
          rows={items}
          getRowKey={(a) => a.id}
          initialSort={{ key: 'priority', dir: 'asc' }}
          dense
        />
      ) : (
        <EmptyState icon={<ListChecks size={26} />} title="No open action items" hint="Nothing flagged against this asset right now." />
      )}
    </SectionCard>
  )
}

/* ----------------------------- History ----------------------------- */
export function HistoryTab({ d, p }: { d: Dataset; p: Property }) {
  const activities = activitiesForRef(d, 'property', p.id)
  return (
    <SectionCard eyebrow="Activity" title="History">
      {activities.length > 0 ? (
        <Timeline activities={activities} />
      ) : (
        <EmptyState title="No activity logged" hint="Notes, calls, and meetings tied to this asset will appear here." />
      )}
    </SectionCard>
  )
}

/* ----------------------------- Files ----------------------------- */
export function FilesTab({ d, p }: { d: Dataset; p: Property }) {
  const files = d.files.filter((f) => f.attachedTo.kind === 'property' && f.attachedTo.id === p.id)
  if (files.length === 0) {
    return (
      <SectionCard eyebrow="Documents" title="Files">
        <EmptyState
          icon={<FolderOpen size={26} />}
          title="No files attached"
          hint="SharePoint and OneDrive documents tied to this asset will surface here."
          action={<Button variant="default" size="sm">Connect SharePoint</Button>}
        />
      </SectionCard>
    )
  }
  return (
    <SectionCard eyebrow="Documents" title="Files" right={<span className="pd-muted-row">{files.length} files</span>}>
      <div className="pd-stack">
        {files.map((f) => (
          <div key={f.id} className="pd-rate-row">
            <div className="pd-rate-label">{f.name}<small>{f.source} · {f.kind.toUpperCase()}</small></div>
            <div className="pd-muted-row">{f.sizeKb != null ? `${num(f.sizeKb)} KB` : ''}</div>
          </div>
        ))}
      </div>
    </SectionCard>
  )
}
